import { sumBy } from "lodash";
import { CustomerModel } from '@/customer/models/CustomerModel';
import { ChannelModel } from '@/channel/models/ChannelModel';
import { AddressModel } from '@/address/models/AddressModel';
import { OrderItemModel } from '@/order/models/OrderItemModel';
import { OrderStatusEnum, IntegrationSourceEnum, getOrderStatusEnumDescription, getIntegrationSourceEnumDescription } from '@c2b/muven-commons';
import { DeliveryCompanyModel } from '../../delivery_company/models/DeliveryCompanyModel';
import { OrderInvoiceModel } from './OrderInvoiceModel';
import { OrderPaymentModel } from './OrderPaymentModel';
import { StoreModel } from '../../store/models/StoreModel';

// Prazo, a partir da venda, para liberar o "Marcar como entregue"
export const DAYS_TO_MARK_AS_DELIVERED = 15

export class OrderModel {
    id?:number = undefined;
    idSource?:string|number = undefined;
    idExternalChannel?:string|number = undefined;
    taxesValue:number = 0;
    incrementValue:number = 0;
    discountValue:number = 0;
    total:number = 0;
    discountCoupon?:string|null = undefined;
    status:OrderStatusEnum = OrderStatusEnum.Integrating;
    modificationSource:IntegrationSourceEnum;
    updatedAt:Date|string|undefined = undefined;
    createdAt:Date|string|undefined = undefined;
    createdAtMuven:Date|string|undefined = undefined;
    customer:Partial<CustomerModel>|undefined = undefined;
    channel:Partial<ChannelModel>|undefined = undefined;
    sellerNotes:string = '';
    addresses:AddressModel[] = [];
    store: StoreModel = new StoreModel();
    items:OrderItemModel[] = [];
    invoices:OrderInvoiceModel[] = [];
    payments:OrderPaymentModel[] = [];
    loadingListAction: boolean = false;

    constructor(o?:Partial<OrderModel>) {
        Object.assign(this, o);

        this._parseCustomer();
        this._parseChannel();
        this._parseUpdatedAt();
        this._parseCreatedAt();
        this._parseAddresses();
        this._parseItems();
        this._parseInvoices();
        this._parseOrderPayments();
        this._parseStore();
    }

    canAddXml():boolean {
        return !this.getInvoice().hasXmlUrl() && 
            (this.isBilled() || this.isApproved())
    }

    canObtainInfoFromChannel():boolean {
        return this.isPending() || 
            this.isApproved() || 
            this.isError() || 
            this.isIntegrating() || 
            this.isPendingAssignment()
    }

    canForceSync():boolean {
        return this.isBilled() || 
            this.isPosted() ||
            this.isDelivered() ||
            this.isCanceled() || 
            this.isFinalized()
    }

    canBeReimported():boolean {
        if (!this.idSource) {
            return true;
        }

        return (
            this.status === OrderStatusEnum.Error ||
            this.status === OrderStatusEnum.Pending ||
            this.status === OrderStatusEnum.Approved ||
            this.status === OrderStatusEnum.Canceled ||
            this.status === OrderStatusEnum.PendingAssignments ||
            this.status === OrderStatusEnum.Finalized
        );
    }

    canBeDeleted():boolean {
        return (
                this.status === OrderStatusEnum.Error ||
                this.status === OrderStatusEnum.Integrating ||
                this.status === OrderStatusEnum.PendingAssignments
            ) && !this.idSource;
    }

    isEqual(order: Partial<OrderModel>):boolean {
        if (!this.idExternalChannel || !order.idExternalChannel) {
            return false;
        }

        return this.idExternalChannel === order.idExternalChannel;
    }

    setStatus(s: OrderStatusEnum):void {
        this.status = s;
    }

    getSellerNotesAsList():string[] {
        if (!this.sellerNotes) {
            return []
        }

        if (!/<br|--/ig.test(this.sellerNotes)) {
            return [
                this.sellerNotes
            ]
        }

        const notesNormalized = this.sellerNotes.replace(/<br>/gi, '<br>')
                                                .replace(/<br >/gi, '<br>')
                                                .replace(/<br \/>/gi, '<br>')
                                                .replace(/<br\/>/gi, '<br>')
                                                .replace(/--/gi, '<br>')

        return notesNormalized.split('<br>').map((str) => {
            return str.trim()
        }).filter((str) => {
            return !!str
        })
    }

    getTotal(): number {
        const increment = this.incrementValue || 0
        const discount = this.discountValue || 0
        const taxes = this.taxesValue || 0
        const freightTotal = this.getFreightTotal() || 0
        const itemsTotal = this.getItemsTotal() || 0

        return itemsTotal + freightTotal + increment + taxes - discount;
    }

    /**
     * Valor do pedido como dá para calcular na listagem: produtos + acréscimos +
     * taxas - descontos. NÃO inclui frete: a listagem não traz invoices nem items,
     * então o getTotal() daria 0 aqui. O `total` gravado pelos agentes é o subtotal
     * dos produtos (o mesmo papel do itemsTotal no getTotal()).
     */
    getListValue():number {
        return (this.total || 0) + (this.incrementValue || 0) + (this.taxesValue || 0) - (this.discountValue || 0)
    }

    hasDiscount():boolean {
        return (this.discountValue || 0) > 0
    }

    getFreightTotal():number {
        return this.getInvoice().freightTotal || 0;
    }

    getItemsTotal(): number {
        let sum = 0;
        
        this.items.forEach((it) => {
            sum += it.getTotal()
        })

        return sum
    }

    hasSellerNotes():boolean {
        return !!this.sellerNotes?.length;
    }

    isPending():boolean {
        return this.status === OrderStatusEnum.Pending ||
               this.status === OrderStatusEnum.PendingMercadoLivreFull;
    }

    isApproved():boolean {
        return this.status === OrderStatusEnum.Approved ||
               this.status === OrderStatusEnum.ApprovedMercadoLivreFull;
    }

    isBilled():boolean {
        return this.status === OrderStatusEnum.Billed ||
               this.status === OrderStatusEnum.BilledMercadoLivreFull;
    }

    isPosted():boolean {
        return this.status === OrderStatusEnum.Posted ||
               this.status === OrderStatusEnum.PostedMercadoLivreFull;
    }

    isDelivered():boolean {
        return this.status === OrderStatusEnum.Delivered;
    }

    isCanceled():boolean {
        return this.status === OrderStatusEnum.Canceled;
    }

    isIntegrating():boolean {
        return this.status === OrderStatusEnum.Integrating;
    }

    isFinalized():boolean {
        return this.status === OrderStatusEnum.Finalized ||
               this.status === OrderStatusEnum.FinalizedMercadoLivreFull;
    }

    isPendingAssignment():boolean {
        return this.status === OrderStatusEnum.PendingAssignments;
    }

    isError():boolean {
        return this.status === OrderStatusEnum.Error;
    }

    hasBillingAddress():boolean {
        return !!this.addresses.find((address) => {
            return address.isBillingAddress();
        });
    }

    getShippingAddress():AddressModel {
        return this.addresses.find((address) => {
            return address.isDeliveryAddress();
        }) || new AddressModel();
    }

    getBillingAddress():AddressModel {
        return this.addresses.find((address) => {
            return address.isBillingAddress();
        }) || new AddressModel();
    }

    getDeliveryCompany():DeliveryCompanyModel {
        if (!this.invoices.length) {
            return new DeliveryCompanyModel()
        }

        if (!this.invoices[0].deliveryCompany) {
            return new DeliveryCompanyModel()
        }

        return this.invoices[0].deliveryCompany || new DeliveryCompanyModel()
    }   

    hasMoreThanOnePayment():boolean {
        return this.payments.length > 1;
    }

    getPayment():OrderPaymentModel {
        if (!this.payments) {
            return new OrderPaymentModel()
        }

        return this.payments[0] || new OrderPaymentModel()
    }

    getInvoice():OrderInvoiceModel {
        if (!this.invoices) {
            return new OrderInvoiceModel()
        }

        return this.invoices[0] || new OrderInvoiceModel()
    }

    hasIdSource():boolean {
        return !!this.idSource   
    }

    /**
     * Data a partir da qual o cliente pode marcar o pedido como entregue à mão
     * (alguns canais nunca informam a entrega e o pedido fica eterno em "Enviado").
     */
    deliveredMarkAvailableAt():Date|null {
        if (!this.createdAt) {
            return null
        }

        const date = new Date(this.createdAt as Date)
        date.setDate(date.getDate() + DAYS_TO_MARK_AS_DELIVERED)

        return date
    }

    canBeMarkedAsDelivered(now:Date = new Date()):boolean {
        const availableAt = this.deliveredMarkAvailableAt()

        return this.isPosted() && !!availableAt && availableAt.getTime() <= now.getTime()
    }

    /**
     * Cancelado antes de chegar à retaguarda: o Muven não envia pedidos
     * cancelados, então ele não está "aguardando", nunca vai descer.
     */
    wasCanceledBeforeBackend():boolean {
        return this.isCanceled() && !this.hasIdSource()
    }

    hasIdExternalChannel():boolean {
        return !!this.idExternalChannel   
    }

    getOrderStatusEnumDescription():string {
        return getOrderStatusEnumDescription(this.status)
    }

    getIntegrationSourceEnumDescription():string {        
        return getIntegrationSourceEnumDescription(this.modificationSource)
    }

    private _parseCustomer():void {
        this.customer = new CustomerModel(this.customer);
    }

    private _parseChannel():void {
        this.channel = new ChannelModel(this.channel);
    }

    private _parseCreatedAt():void {
        if (typeof this.createdAt === 'string') {
            this.createdAt = new Date(this.createdAt);
        }
    }

    private _parseUpdatedAt():void {
        if (typeof this.updatedAt === 'string') {
            this.updatedAt = new Date(this.updatedAt);
        }
    }

    private _parseAddresses():void {
        this.addresses = this.addresses.map((address) => {
            return new AddressModel(address);
        });
    }

    private _parseItems():void {
        this.items = this.items.map((item) => {
            return new OrderItemModel(item);
        });
    }

    private _parseInvoices():void {
        this.invoices = this.invoices.map((item) => {
            return new OrderInvoiceModel(item);
        });
    }

    private _parseOrderPayments():void {
        this.payments = this.payments.map((item) => {
            return new OrderPaymentModel(item);
        });
    }

    private _parseStore():void {
        this.store = new StoreModel(this.store);
    }
}
